import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { IWindowManagerMessageData, IWindowManagerMessageOptions, WindowReceivedEvent } from '@ngt/redact/src/WindowManager';

import PdfViewer from '@/components/pdf/PdfViewer';
import ErrorContext from '@/context/ErrorContext';
import Loading from '@/components/layout/Loading';
import Hidden from '@/components/layout/Hidden';

const RedactWindowPage: FC = () => {

    const [queryParameters] = useSearchParams();

    const { addError } = useContext(ErrorContext);

    const instId = useRef<string | undefined>();
    const actionedRef = useRef<boolean>(false);
    const origin = useRef<string | undefined>();

    const initRequest = useRef({ requested: false, requesting: false });

    const [file, setFile] = useState<File | undefined>(undefined);
    const [options, setOptions] = useState<IWindowManagerMessageOptions | undefined>(undefined);

    const [viewerLoading, setViewerLoading] = useState<boolean>(true);

    const onConfirmClicked = useCallback(() => {
        setViewerLoading(true);
    }, [setViewerLoading]);

    const onPdfLoaded = useCallback(() => {
        setViewerLoading(false);
    }, [setViewerLoading]);

    const isPdf = useCallback((file: File) => {
        // If file has a MIME type
        if (file.type && file.type !== "") {
            if (file.type === "application/pdf") {
                return true;
            }

            // If no mime type, check file name
        } else {
            let name = file.name;

            let idx = name.lastIndexOf('.');

            if (name.substring(idx + 1, name.length) === 'pdf') {
                return true;
            }
        }

        return false;
    }, [])

    const onConfirm = useCallback((file: File) => {
        actionedRef.current = true;
        window.opener.postMessage({ instanceId: instId.current, event: WindowReceivedEvent.Update, payload: file }, origin.current);
    }, [file, actionedRef]);

    const onCancel = useCallback(() => {
        actionedRef.current = true;
        window.opener.postMessage({ instanceId: instId.current, event: WindowReceivedEvent.Finish, payload: "cancel finish"  }, origin.current);
    }, [file, actionedRef])

    // Init
    useEffect(() => {

        const handleEventListener = (message: MessageEvent<IWindowManagerMessageData>) => {
            // If the message is missing the origin
            if (!origin.current) {
                addError({
                    message: 'Missing caller origin'
                });
    
                return;
            }
    
            if (message.data.event) {
                // If we received the update message
                if (message.data.event === WindowReceivedEvent.Update) {
                    initRequest.current = {
                        requested: true, 
                        requesting: false 
                    };
    
                    if (!message.data.payload ||
                        !(message.data.payload instanceof File)) {
    
                        addError({
                            message: 'Did not receive file'
                        });
                    } 
                    else if ((message.data.payload instanceof File) &&
                        !isPdf(message.data.payload)) {
    
                        addError({
                            message: 'File is of incorrect format'
                        });
                    } 
                    else {
                        setFile(message.data.payload);
    
                        if (message.data.options) {
                            setOptions(message.data.options);
                        }
                    }
    
                    return;
                }

                // If we received a finish event
                if (message.data.event === WindowReceivedEvent.Finish) {
                    actionedRef.current = true;
                    setViewerLoading(false);
                    window.opener.postMessage({ instanceId: instId.current, event: WindowReceivedEvent.Finish, payload: "finish event"  }, origin.current);
                }
    
                // If we received an error message
                if (message.data.event === WindowReceivedEvent.Error) {

                    setViewerLoading(false);
    
                    if (message.data.payload) {
                        addError({
                            message: message.data.payload
                        });
                    } else {
                        addError({
                            message: "An error occurred on the callers end"
                        });
                    }
    
                    return;
                }
            }
        }
        
        window.addEventListener('message', handleEventListener);

        const triggerOnFinish = () => {
            if (!actionedRef.current) {
                setViewerLoading(false);
                window.opener.postMessage({ instanceId: instId.current, event: WindowReceivedEvent.Finish, payload: 'finish call' }, origin.current);
            }
        }

        window.addEventListener("beforeunload", triggerOnFinish);

        // If this window has a parent, configure event listener
        if (window.opener && !initRequest.current.requested && !initRequest.current.requesting) {
            var instanceid = queryParameters.get("instanceid");
            origin.current = queryParameters.get("origin") ?? "";

            instId.current = instanceid === null ? undefined : instanceid;

            initRequest.current.requesting = true;

            // If haven't made the initial request yet
            if (instId.current) {
                window.opener.postMessage({ instanceId: instId.current, event: WindowReceivedEvent.Init }, origin.current ?? "");
            }
        }

        return () => {
            window.removeEventListener("beforeunload", triggerOnFinish);
            window.removeEventListener('message', handleEventListener);
        }

    }, []);

    return (
        <Box>
            <Hidden hide={true} />
            <Loading name="file" isLoading={!file} isValidating={false} />
            {
                file && onConfirm && onCancel &&
                <PdfViewer 
                    file={file} 
                    onConfirm={onConfirm} 
                    onCancel={onCancel}
                    redactOptions={options}
                    loading={viewerLoading}
                    onConfirmClicked={onConfirmClicked}
                    onPdfLoaded={onPdfLoaded}
                />
            }
        </Box>
    );
}

export default RedactWindowPage;